import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { commsService } from '../services/commsService';
import { patientService } from '../services/patientService';

const TopBar = ({ user, showProfile = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);
  const searchRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimer = useRef(null);

  // Generate breadcrumbs based on current route
  const getBreadcrumbs = () => {
    const path = location.pathname;
    const breadcrumbs = [];

    // Route-based breadcrumb mapping
    const routeMap = {
      '/dashboard': [{ label: 'Dashboard', active: true }],
      '/patients': [{ label: 'Patients', active: true }],
      '/patient/new': [{ label: 'Patients', active: false, path: '/patients' }, { label: 'New Patient', active: true }],
      '/add-patient': [{ label: 'Patients', active: false, path: '/patients' }, { label: 'Add Patient', active: true }],
      '/intake-forms': [{ label: 'Intake Forms', active: true }],
      '/intake-form': [{ label: 'Intake Forms', active: false, path: '/intake-forms' }, { label: 'Intake Form', active: true }],
      '/appointments': [{ label: 'Appointments', active: true }],
      '/analysis': [{ label: 'Analysis', active: true }],
      '/billing': [{ label: 'Billing', active: true }],
      '/progress-notes': [{ label: 'Progress Notes', active: true }],
      '/tasks': [{ label: 'Tasks', active: true }],
      '/clinical-ops': [{ label: 'Clinical Ops', active: true }],
      '/settings': [{ label: 'Settings', active: true }],
      '/form-builder': [{ label: 'Form Builder', active: true }],
      '/pricing-plans': [{ label: 'Pricing Plans', active: true }],
      '/payment-details': [{ label: 'Payment Details', active: true }],
      '/onboarding/organization': [{ label: 'Onboarding', active: false }, { label: 'Organization', active: true }],
      '/onboarding/personal': [{ label: 'Onboarding', active: false }, { label: 'Personal Info', active: true }],
      '/onboarding/preferences': [{ label: 'Onboarding', active: false }, { label: 'Preferences', active: true }],
    };

    // Check for exact match first
    if (routeMap[path]) {
      return routeMap[path];
    }

    // Check for dynamic routes (e.g., /patient/:id, /intake-form/view/:id)
    if (path.startsWith('/patient/edit/')) {
      return [{ label: 'Patients', active: false, path: '/patients' }, { label: 'Edit Patient', active: true }];
    }
    if (path.match(/^\/patient\/[^/]+$/)) {
      return [{ label: 'Patients', active: false, path: '/patients' }, { label: 'Patient Details', active: true }];
    }
    if (path.startsWith('/intake-form/view/')) {
      return [{ label: 'Intake Forms', active: false, path: '/intake-forms' }, { label: 'View Form', active: true }];
    }
    if (path.startsWith('/intake-form/edit/')) {
      return [{ label: 'Intake Forms', active: false, path: '/intake-forms' }, { label: 'Edit Form', active: true }];
    }
    if (path.match(/^\/intake-form\/[^/]+$/)) {
      return [{ label: 'Intake Forms', active: false, path: '/intake-forms' }, { label: 'New Form', active: true }];
    }

    // Default fallback
    return [{ label: 'Dashboard', active: true, path: '/dashboard' }];
  };

  const breadcrumbs = getBreadcrumbs();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runClientSearch = (q) => {
    setSearchQ(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || q.trim().length < 2) {
      setSearchHits([]);
      setSearchOpen(false);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await patientService.getPatients({ search: q.trim(), limit: 8 });
        const list = Array.isArray(res) ? res : res.data || [];
        setSearchHits(list);
        setSearchOpen(true);
      } catch {
        setSearchHits([]);
      }
    }, 250);
  };

  const refreshNotifications = async () => {
    try {
      if (!authService.isAuthenticated()) return;
      const [list, count] = await Promise.all([
        commsService.listNotifications(),
        commsService.unreadCount(),
      ]);
      setNotifications(list.data || []);
      setUnreadCount(count.data?.count || 0);
    } catch {
      // silent — backend may be offline during early boot
    }
  };

  useEffect(() => {
    refreshNotifications();
    const id = setInterval(refreshNotifications, 60000);
    const onNotif = () => refreshNotifications();
    window.addEventListener('peleescribe:notification', onNotif);
    return () => {
      clearInterval(id);
      window.removeEventListener('peleescribe:notification', onNotif);
    };
  }, []);

  const toggleNotif = async () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next) await refreshNotifications();
  };

  const markAllRead = async () => {
    try {
      await commsService.markAllRead();
      await refreshNotifications();
    } catch (e) {
      console.error(e);
    }
  };

  const openNotification = async (n) => {
    try {
      if (!n.isRead) await commsService.markRead(n._id);
      setNotifOpen(false);
      await refreshNotifications();
      if (n.linkPath) navigate(n.linkPath);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      // If user is provided as prop, use it
      if (user) {
        setCurrentUser(user);
        return;
      }

      // Otherwise, try to fetch fresh user data from API
      try {
        if (authService.isAuthenticated()) {
          const response = await authService.getProfile();
          if (response && response.user) {
            setCurrentUser(response.user);
          } else {
            // Fallback to localStorage if API fails
            const userData = authService.getCurrentUser();
            setCurrentUser(userData);
          }
        } else {
          const userData = authService.getCurrentUser();
          setCurrentUser(userData);
        }
      } catch (error) {
        console.error('Error fetching user profile in TopBar:', error);
        // Fallback to localStorage if API fails
        const userData = authService.getCurrentUser();
        setCurrentUser(userData);
      }
    };

    fetchUserData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('onboardingOrganization');
      localStorage.removeItem('onboardingPersonal');
      localStorage.removeItem('onboardingPreferences');
      navigate('/login');
    }
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleProfileView = () => {
    setIsDropdownOpen(false);
    navigate('/settings');
  };

  return (
    <div className="sticky top-0 flex justify-between items-center px-3 sm:px-5 py-2 sm:py-3.5 bg-white border-b border-gray-200 h-[60px] sm:h-[70px] z-40">
      <div className="flex items-center">
        <div className="flex items-center gap-1 sm:gap-2">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <span className="text-gray-300 text-[10px] sm:text-xs hidden md:inline">›</span>
              )}
              {crumb.active ? (
                <span className="font-poppins font-bold text-xs sm:text-sm text-primary hidden md:inline">
                  {crumb.label}
                </span>
              ) : (
                <span
                  className="font-oxygen font-light text-[10px] sm:text-xs text-gray-300 hidden md:inline cursor-pointer hover:text-primary transition-colors"
                  onClick={() => crumb.path && navigate(crumb.path)}
                >
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 lg:gap-5">
        {/* Global client search */}
        <div className="relative hidden md:block" ref={searchRef}>
          <input
            type="search"
            value={searchQ}
            onChange={(e) => runClientSearch(e.target.value)}
            onFocus={() => searchHits.length && setSearchOpen(true)}
            placeholder="Search clients…"
            className="w-40 lg:w-56 border border-gray-200 rounded-full px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Search clients"
          />
          {searchOpen && (
            <div className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
              {searchHits.length === 0 ? (
                <p className="p-3 text-xs text-gray-500">No clients found</p>
              ) : (
                searchHits.map((p) => (
                  <button
                    key={p._id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50"
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchQ('');
                      navigate(`/patient/${p._id}`);
                    }}
                  >
                    <div className="font-medium text-gray-800">
                      {p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ')}
                    </div>
                    <div className="text-xs text-gray-500">{p.email || p.phoneNumber || ''}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Upgrade/Trial Button - Show ONLY for new trial accounts that haven't selected a plan yet */}
        {currentUser && !currentUser.selectedPlan && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              console.log('TopBar upgrade button clicked - navigating to pricing plans');
              navigate('/pricing-plans');
            }}
            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-[#25b6dd] to-[#1a8ba8] text-white rounded-lg text-xs sm:text-sm font-poppins font-medium hover:opacity-90 transition-opacity shadow-md hover:shadow-lg cursor-pointer"
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              Upgrade Plan
            </span>
          </button>
        )}

        {/* Show current plan badge if user has selected a plan (regardless of payment status) */}
        {currentUser && currentUser.selectedPlan && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              console.log('Plan badge clicked - navigating to pricing plans');
              navigate('/pricing-plans');
            }}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#25b6dd]/10 to-[#1a8ba8]/10 rounded-lg border border-[#25b6dd]/30 hover:from-[#25b6dd]/20 hover:to-[#1a8ba8]/20 transition-all cursor-pointer"
          >
            <svg className="w-3.5 h-3.5 text-[#1a8ba8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            <span className="text-xs font-poppins font-semibold text-[#1a8ba8]">
              Upgrade Plan
            </span>
            <svg className="w-3 h-3 text-[#1a8ba8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Notification bell */}
        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={toggleNotif}
            className="relative flex items-center justify-center w-8 sm:w-10 h-8 sm:h-10 bg-white border border-gray-200 rounded-full cursor-pointer shadow-sm hover:bg-gray-50"
            aria-label="Notifications"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zM13.33 11.33A1.33 1.33 0 0 1 12 12H4a1.33 1.33 0 0 1-1.33-1.33V10c0-3.33 2-6 5.33-6s5.33 2.67 5.33 6v1.33z" stroke="#1E88E5"/>
              <path d="M10 5.33A2 2 0 0 0 8 3.33a2 2 0 0 0-2 2" stroke="#1E88E5"/>
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-danger text-white text-[10px] rounded-full flex items-center justify-center font-semibold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 mt-2 w-80 sm:w-96 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="flex items-center justify-between px-3 py-2 border-b">
                <span className="text-sm font-semibold text-gray-800">Notifications</span>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={markAllRead}
                >
                  Mark all read
                </button>
              </div>
              {notifications.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">No notifications yet.</p>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n._id}
                    type="button"
                    className={`w-full text-left px-3 py-2 border-b border-gray-50 hover:bg-gray-50 ${
                      n.isRead ? 'opacity-70' : 'bg-blue-50/40'
                    }`}
                    onClick={() => openNotification(n)}
                  >
                    <div className="text-sm font-medium text-gray-800">{n.title}</div>
                    <div className="text-xs text-gray-500 line-clamp-2">{n.message}</div>
                    <div className="text-[10px] text-gray-400 mt-1">
                      {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                      {n.category ? ` · ${n.category}` : ''}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="relative" ref={dropdownRef}>
          <div
            className="w-7 sm:w-8 h-7 sm:h-8 rounded-full overflow-hidden border border-primary relative cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleProfileClick}
          >
            <img
              src="/img/default-avatar.png"
              alt="User"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden w-full h-full bg-gradient-to-r from-primary to-secondary items-center justify-center font-poppins font-semibold text-xs sm:text-sm text-white">
              {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : currentUser?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
          </div>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
              {showProfile && (
                <>
                  <button
                    onClick={handleProfileView}
                    className="w-full px-4 py-2 text-left font-poppins text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8 10c-3.33 0-6 2-6 4.5V16h12v-1.5c0-2.5-2.67-4.5-6-4.5z" fill="#424242"/>
                    </svg>
                    Profile
                  </button>
                  <div className="border-t border-gray-200"></div>
                </>
              )}
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left font-poppins text-sm text-danger hover:bg-red-50 flex items-center gap-2 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 14H3.33A1.33 1.33 0 0 1 2 12.67V3.33A1.33 1.33 0 0 1 3.33 2H6M10.67 11.33 14 8l-3.33-3.33M14 8H6" stroke="#FF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopBar;